import { valueAtPath } from "@/src/lib/utils/value-at-path";

import type { z } from "zod";

/**
 * Maps a Zod issue to the short, form-friendly copy our editable cards and forms render inline
 * underneath the offending input.
 *
 * Zod v4 rewrote its default messages, and two of the new wordings are unusable as inline field
 * errors:
 *
 *   - a missing scalar now reads `Invalid input: expected string, received undefined` where v3 said
 *     `Required`;
 *   - a missing enum now enumerates every permitted option, so an absent `serviceLine` renders all
 *     69 `ServiceLine` values (~900 characters) under a single field.
 *
 * Zod finalizes issues without the offending input attached, and a missing key is distinguishable
 * from a wrong-typed one only by the English message text. So resolve the value at `issue.path`
 * against the object that was parsed instead of pattern-matching the message: absent means
 * `Required`, and everything else falls through to Zod's own message (a custom `error`/`message`
 * declared on the schema still wins).
 */
export function formatZodIssueMessage(issue: z.core.$ZodIssue, parsedValue: unknown): string {
  const isAbsent = issue.code === "invalid_type" || issue.code === "invalid_value";

  if (isAbsent && valueAtPath(parsedValue, issue.path) === undefined) {
    return "Required";
  }

  return issue.message;
}
